import { headerAuth, oauthAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import { exactUrlToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

const defaultUrlPattern = ({ hostname }: Record<string, string>) =>
  exactUrlToRegex(hostname).replace(/\/\.\*$/, "/api/v1/.*");

const publicEnvVars = ({ hostname }: Record<string, string>) => ({
  EXTRAHOP_URL: hostname ? `https://${hostname}` : "",
});

export const extraHop = defineProduct({
  name: "ExtraHop",
  tagLine: "Network detection and response",
  keywords: "RevealX Reveal(x) 360 Enterprise NDR network security",
  logo,
  tags: ["security"],
  variants: [
    productVariant({
      identifier: "extraHop",
      authLabel: "RevealX 360 (OAuth 2.0)",
      protocol: "http",
      defaultUrlPattern,
      auth: oauthAuth,
      steps: [
        {
          title: "Create RevealX 360 API credentials",
          items: [
            {
              kind: "markdown",
              content:
                "In RevealX 360, open **System Settings > All Administration > API Access**. An administrator must enable API access and select **Create Credentials**. Choose the privileges your workflows need; **Full Read-Only** is sufficient for the connection test. Copy the **ID** and **Secret** before closing the dialog. The secret is shown only once.",
            },
          ],
        },
        {
          title: "Enter your RevealX 360 details",
          items: [
            {
              kind: "hostname",
              label: "API hostname",
              placeholder: "example.api.cloud.extrahop.com",
              required: true,
              target: { kind: "configuration", field: "hostname" },
              helperText:
                "Copy the hostname from API Endpoint on the API Access page, without https:// or /oauth2/token. This is different from your RevealX 360 console hostname.",
            },
            {
              kind: "input",
              label: "Client ID",
              required: true,
              target: { kind: "oauthConnection", field: "clientId" },
            },
            {
              kind: "input",
              label: "Client secret",
              required: true,
              target: { kind: "oauthConnection", field: "clientSecret" },
            },
          ],
        },
      ],
      oauth: {
        grantType: () => "client_credentials",
        tokenEndpoint: ({ hostname }) => `https://${hostname}/oauth2/token`,
        tokenEndpointAuthMethod: () => "client_secret_basic_no_urlencode",
        clientCredentialsFrom: "db",
        scopes: () => [],
      },
      connectionTest: {
        protocol: "http",
        request: ({ hostname }) => ({
          url: `https://${hostname}/api/v1/devices/search`,
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ limit: 1 }),
          expectedStatus: 200,
        }),
      },
      publicEnvVars,
    }),
    productVariant({
      identifier: "extraHopSelfManaged",
      authLabel: "Self-managed (API key)",
      protocol: "http",
      defaultUrlPattern,
      auth: (app) =>
        headerAuth([
          {
            name: "Authorization",
            value: `ExtraHop apikey=${app.secret.apiKey}`,
          },
        ]),
      steps: [
        {
          title: "Generate an ExtraHop API key",
          items: [
            {
              kind: "markdown",
              content:
                "Open your ExtraHop system’s **Administration** settings at **https://<hostname>/admin**, then go to **Access Settings > API Access**. Your administrator must allow API key generation. Under **Generate an API Key**, enter a description and select **Generate**. Copy the key from **API Keys**. The key inherits your user account’s privileges.",
            },
          ],
        },
        {
          title: "Enter your ExtraHop details",
          items: [
            {
              kind: "input",
              label: "Hostname",
              placeholder: "extrahop.example.com",
              required: true,
              target: { kind: "configuration", field: "hostname" },
              helperText:
                "Your ExtraHop hostname or IP address, including any port, without https:// or a path.",
            },
            {
              kind: "input",
              label: "API key",
              required: true,
              target: { kind: "secrets", field: "apiKey" },
            },
          ],
        },
      ],
      connectionTest: {
        protocol: "http",
        request: ({ hostname }) => ({
          url: `https://${hostname}/api/v1/extrahop`,
          expectedStatus: 200,
        }),
      },
      publicEnvVars,
    }),
  ],
});
